/** @jsxImportSource @emotion/react */
import { css } from "@emotion/react";
import styled from "@emotion/styled";
import { Spacer } from "components/general/Spacer/Spacer";
import { cloneDeep, partition } from "lodash-es";
import React, { useEffect, useMemo, useState } from "react";
import ReactModal from "react-modal";
import { Account, parseUnits, Transport, WalletClient } from "viem";
import { Chain } from "viem/chains";
import { useWalletClient } from "wagmi";
import { getContract, waitForTransaction } from "wagmi/actions";
import { NAV_LINKS } from "../../../../config/consts";
import { useGetTokenDecimals } from "../../../../hooks/useGetTokenDecimals";
import { useTimedQuoteRefetch } from "../../../../hooks/useTimedQuoteRefetch";
import { GlobalStore } from "../../../../store/global/GlobalStore";
import { Token, WRAPPED_ABI } from "../../../../store/global/TokenEnum";
import { TokenMetadata } from "../../../../store/global/getTokenMetadata";
import { Amount } from "../../../../types/amount";
import { OrderStatus } from "../../../../types/orderStatus";
import {
  getAmountNeededFromQuote,
  getTokensToApprove,
  shouldRevokeFirst,
} from "../../../../utils/allowanceUtils";
import { getWrappedSymbolForChain } from "../../../../utils/chainUtils";
import { getTokenAddressBySymbol } from "../../../../utils/orderUtils";
import { getTokenIcon } from "../../../../utils/tokenIconUtils";
import { getNameForToken } from "../../../../utils/tokenMetadataUtils";
import { isNativeToken } from "../../../../utils/wrapUtils";
import { Text } from "../../../general/Text";
import { ConfirmInWallet } from "../Allowance/ConfirmInWallet";
import { ProcessingTransaction } from "../Allowance/ProcessingTransaction";
import { TryAgain } from "../Allowance/TryAgain";
import { MODAL_SELECTORS } from "../modalSelectors";
import { CompletedTransaction } from "./elements/CompletedTransaction";
import { WrapModalContent } from "./elements/WrapModalContent";
import { ApprovalStore } from "../../../../store/approvals/ApprovalsStore";

export interface WrappingQuoteModalProps {
  ariaHideApp: boolean;
  modalSelector: string;
}

export enum WrapQuoteStatus {
  PENDING_WALLET_CONFIRM = "PENDING_WALLET_CONFIRM",
  PROCESSING_TRANSACTION = "PROCESSING_TRANSACTION",
  REJECTED_WRAP = "REJECTED_WRAP",
  COMPLETED = "COMPLETED",
}

export const WrappingQuoteModal: React.FC<WrappingQuoteModalProps> = ({
  ariaHideApp,
  modalSelector,
}) => {
  const [wrapQuoteStatus, setWrapQuoteStatus] = useState<WrapQuoteStatus>(
    WrapQuoteStatus.PENDING_WALLET_CONFIRM
  );
  const {
    network,
    tokens: tokenMetadata,
    quote,
    allowances,
  } = GlobalStore.useState(s => ({
    network: s.network,
    tokens: s.tokens,
    quote: s.quote,
    allowances: s.allowances,
  }));
  const MODAL_DESCRIPTION = `To trade ${getNameForToken(
    network.nativeCurrency.symbol as Token,
    tokenMetadata
  )} it
  needs to be converted to
  ${getNameForToken(getWrappedSymbolForChain(network), tokenMetadata)} first.`;
  const nativeIcon = useMemo(
    () => getTokenIcon(network.nativeCurrency.symbol as Token, tokenMetadata),
    [network.nativeCurrency.symbol, tokenMetadata]
  );
  const theme = GlobalStore.useState(s => s.theme);
  const wrappedIcon = useMemo(
    () => getTokenIcon(getWrappedSymbolForChain(network), tokenMetadata),
    [network, tokenMetadata]
  );
  const { data: walletClient } = useWalletClient();
  const getTokenDecimals = useGetTokenDecimals();
  const [tx, setTx] = useState<string>("");
  const [hasRefetchedFreshQuote, setHasRefetchedFreshQuote] = useState<boolean>(false);
  const timedQuoteRefetch = useTimedQuoteRefetch();

  const wrap = async (
    network: Chain,
    tokenMetadata: TokenMetadata,
    walletClient: WalletClient<Transport, Chain, Account>,
    amountToWrap: Amount
  ) => {
    setWrapQuoteStatus(WrapQuoteStatus.PENDING_WALLET_CONFIRM);
    const amountToWrapBigInt = parseUnits(
      amountToWrap.value.toString() as `${number}`,
      getTokenDecimals(getWrappedSymbolForChain(network))
    );
    const contract = getContract({
      address: getTokenAddressBySymbol(tokenMetadata, network, getWrappedSymbolForChain(network)),
      abi: WRAPPED_ABI,
      walletClient,
    });
    const txHash = await contract.write.deposit({ value: amountToWrapBigInt }).catch(e => {
      setWrapQuoteStatus(WrapQuoteStatus.REJECTED_WRAP);
    });
    if (txHash) {
      setTx(txHash);
      setWrapQuoteStatus(WrapQuoteStatus.PROCESSING_TRANSACTION);
      const transaction = await waitForTransaction({
        hash: txHash,
      });
      if (transaction?.status === "success") {
        setWrapQuoteStatus(WrapQuoteStatus.COMPLETED);

        // Switches the token to Wrapped token
        GlobalStore.update(s => {
          s.takerTokens.map(el =>
            isNativeToken(el.token, network) ? (el.token = getWrappedSymbolForChain(network)) : ""
          );
        });
      }
    }
  };

  // Opens on initial press of "continue" from action button we straight away open wrap wallet prompt
  useEffect(() => {
    if (walletClient && tokenMetadata) {
      const amountToWrap = getAmountNeededFromQuote(quote, getWrappedSymbolForChain(network));
      void wrap(network, tokenMetadata, walletClient, amountToWrap);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [walletClient]);

  /*
   *  We have logic in reactions.ts which clear the quote when the STORE taker or maker tokens change this is required logic from changing when you are in token selector
   *  We force refresh here and set a flag that we have re-fetched so we know below this hook to send the user to the approval modal if they need to approve.
   *  The reason for doing it here instead of adding a flag of don't clear quote when in "WRAPPING" modal to reactions is to keep logic inside this file
   *  and so we don't unexpectedly not clear quote when we need to.
   * */

  useEffect(() => {
    if (quote === undefined && tx) {
      timedQuoteRefetch
        .forceRefresh()
        .then(() => {
          setHasRefetchedFreshQuote(true);
        })
        .catch(e => {
          console.error(".", e);
        });
    }
  }, [quote, tx, timedQuoteRefetch]);

  useEffect(() => {
    if (hasRefetchedFreshQuote && tx) {
      setTimeout(() => {
        GlobalStore.update(s => {
          const tokensToApprove = getTokensToApprove(cloneDeep(s.quote), allowances);
          const requiredSignatures =
            GlobalStore.getRawState().quote?.requiredSignatures?.length ?? 0;
          console.log(`tokensToApprove`, tokensToApprove);
          console.log(`requiredSignatures`, requiredSignatures);
          const approvalsRequired = tokensToApprove.length > 0 || requiredSignatures > 0;
          if (approvalsRequired) {
            const [revokeFirst, increase] = partition(tokensToApprove, entry =>
              shouldRevokeFirst(entry.symbol, allowances)
            );
            ApprovalStore.update(s => {
              s.requiredSignatures = GlobalStore.getRawState().quote?.requiredSignatures ?? [];
              s.tokensToApprove = tokensToApprove;
              s.initialAllowances = allowances;
              s.currentAllowances = allowances;
              s.tokensByFlow = {
                approve: increase,
                revoke: revokeFirst,
              };
              s.permitType = GlobalStore.getRawState().permitType;
              s.tokenMetadata = GlobalStore.getRawState().tokens ?? {};
            });
          }

          s.modalSelector = approvalsRequired ? MODAL_SELECTORS.APPROVE_ALLOWANCE : undefined;
          if (tokensToApprove.length === 0 && requiredSignatures === 0) {
            s.orderStatus = OrderStatus.REVIEW;
          }
        });
      }, 2000);
    }
  }, [hasRefetchedFreshQuote, tx, allowances]);
  const WrappedAndNativeIcon = () => {
    return (
      <IconWrapper>
        <img src={nativeIcon} width={80} height={80} alt={"Native token icon"} />
        <WrapIcon src={wrappedIcon} width={80} height={80} />
      </IconWrapper>
    );
  };

  const handleClose = () => {
    GlobalStore.update(s => (s.modalSelector = undefined));
  };

  return (
    <ReactModal
      isOpen={modalSelector === MODAL_SELECTORS.WRAPPING_QUOTE}
      ariaHideApp={ariaHideApp}
      className={"react-modal new-skin modal-dialog modal-dialog-centered m-frame"}
      shouldCloseOnOverlayClick={true}
      style={{
        content: {
          margin: "0 auto",
          marginTop: 80,
        },
      }}
      onRequestClose={handleClose}
    >
      <WrapModalContent>
        <ModalWrapper>
          <ContentWrapper>
            <WrappedAndNativeIcon />
            <Text textStyle="Title" size={"Medium"} color={theme.font.primary.default}>
              Wrap your {network.nativeCurrency.symbol} to {getWrappedSymbolForChain(network)}
            </Text>
            <div>
              <Text
                textStyle="Paragraph"
                size="Large"
                textCss={css`
                  max-width: 300px;
                `}
                color={theme.font.primary.default}
              >
                {MODAL_DESCRIPTION}
              </Text>
              <Spacer height={12} />
              <Text textStyle="Title" size="XS">
                <LearnMoreLink href={NAV_LINKS.FAQ} target="_blank" rel="noreferrer noopener">
                  Learn more about wrapping
                </LearnMoreLink>
              </Text>
            </div>
          </ContentWrapper>
          <ConfirmInWallet
            isVisible={wrapQuoteStatus === WrapQuoteStatus.PENDING_WALLET_CONFIRM}
            confirmOrApproveText={"Confirm"}
          />
          <TryAgain
            isVisible={wrapQuoteStatus === WrapQuoteStatus.REJECTED_WRAP}
            onClick={() => {
              if (walletClient && tokenMetadata) {
                void wrap(
                  network,
                  tokenMetadata,
                  walletClient,
                  getAmountNeededFromQuote(quote, getWrappedSymbolForChain(network))
                );
              }
            }}
          />
          <ProcessingTransaction
            isVisible={wrapQuoteStatus === WrapQuoteStatus.PROCESSING_TRANSACTION}
            tx={tx}
          />
          <CompletedTransaction isVisible={wrapQuoteStatus === WrapQuoteStatus.COMPLETED} tx={tx} />
        </ModalWrapper>
      </WrapModalContent>
    </ReactModal>
  );
};

export const ModalWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  gap: 10px;
  padding: 15px;
  text-align: center;
`;

export const ContentWrapper = styled.div`
  display: flex;
  padding: 10px 0px;
  flex-direction: column;
  align-items: center;
  gap: 16px;
  align-self: stretch;
`;

export const WrapIcon = styled.img`
  position: absolute;
  top: 35%;
  left: 45%;
`;
export const IconWrapper = styled.div`
  width: 130px;
  height: 130px;
  padding: 13px;
  display: flex;
  position: relative;

  img {
    filter: drop-shadow(0px 5px 20px rgba(0, 0, 0, 1));
  }
`;

export const LearnMoreLink = styled.a`
  color: #38da7c;
`;
